import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

const PWAInstallPrompt = () => {
  const { t } = useTranslation();
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [platform, setPlatform] = useState("");
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed
    const checkStandalone = () => {
      const standalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone ||
        document.referrer.includes("android-app://");
      setIsStandalone(standalone);
    };

    checkStandalone();

    // Detect platform
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setPlatform("ios");
    } else if (/android/.test(userAgent)) {
      setPlatform("android");
    } else if (/windows/.test(userAgent)) {
      setPlatform("windows");
    } else if (/macintosh|mac os x/.test(userAgent)) {
      setPlatform("macos");
    } else {
      setPlatform("desktop");
    }

    // Check if prompt was recently dismissed
    const dismissedTime = localStorage.getItem("pwaPromptDismissed");
    const now = Date.now();
    const oneWeek = 7 * 24 * 60 * 60 * 1000;

    // Debug mode: add ?pwa=test to URL to force show prompt
    const urlParams = new URLSearchParams(window.location.search);
    const debugMode = urlParams.get("pwa") === "test";

    if (
      !debugMode &&
      dismissedTime &&
      now - parseInt(dismissedTime) < oneWeek
    ) {
      return;
    }

    // Never pop over an open modal (react-bootstrap marks <body> with
    // "modal-open") — the prompt would cover the modal's own buttons.
    // Re-check until the user is done with whatever dialog they're in.
    const showWhenNoModalOpen = () => {
      if (document.body.classList.contains("modal-open")) {
        setTimeout(showWhenNoModalOpen, debugMode ? 1000 : 15000);
        return;
      }
      setShowPrompt(true);
    };

    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);

      // Show custom prompt after 30 seconds on first visit
      const firstVisit = !localStorage.getItem("hasVisited");
      if (firstVisit) {
        localStorage.setItem("hasVisited", "true");
        setTimeout(showWhenNoModalOpen, debugMode ? 1000 : 30000);
      } else {
        // Show after 2 minutes on return visits
        setTimeout(showWhenNoModalOpen, debugMode ? 1000 : 120000);
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // For iOS, show prompt after delay if not installed
    if (platform === "ios" && !isStandalone) {
      const firstVisit = !localStorage.getItem("hasVisited");
      if (firstVisit) {
        localStorage.setItem("hasVisited", "true");
        setTimeout(showWhenNoModalOpen, debugMode ? 1000 : 30000);
      }
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === "accepted") {
        console.log("User accepted the install prompt");
        // Track installation
        localStorage.setItem("pwaInstalled", "true");
      }

      // Clear the deferred prompt
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem("pwaPromptDismissed", Date.now().toString());
  };

  if (!showPrompt || isStandalone) return null;

  // Platform-specific install instructions
  const getInstallInstructions = () => {
    switch (platform) {
      case "ios":
        return (
          <div>
            <p className="text-muted mb-3 fs-7">
              {t("pwa.install.instructions")}
            </p>
            <div className="d-flex flex-column gap-2">
              <div className="d-flex align-items-start gap-3">
                <div className="bg-primary bg-opacity-10 p-2 rounded">
                  <i className="bi bi-share text-primary"></i>
                </div>
                <div>
                  <p className="mb-1 fw-medium fs-7">
                    {t("pwa.ios.step1.title")}
                  </p>
                  <p className="text-muted mb-0 fs-8">
                    {t("pwa.ios.step1.subtitle")}
                  </p>
                </div>
              </div>
              <div className="d-flex align-items-start gap-3">
                <div className="bg-primary bg-opacity-10 p-2 rounded">
                  <i className="bi bi-plus-lg text-primary"></i>
                </div>
                <div>
                  <p className="mb-1 fw-medium fs-7">
                    {t("pwa.ios.step2.title")}
                  </p>
                  <p className="text-muted mb-0 fs-8">
                    {t("pwa.ios.step2.subtitle")}
                  </p>
                </div>
              </div>
              <div className="d-flex align-items-start gap-3">
                <div className="bg-primary bg-opacity-10 p-2 rounded">
                  <i className="bi bi-phone text-primary"></i>
                </div>
                <div>
                  <p className="mb-1 fw-medium fs-7">
                    {t("pwa.ios.step3.title")}
                  </p>
                  <p className="text-muted mb-0 fs-8">
                    {t("pwa.ios.step3.subtitle")}
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case "android":
        return (
          <div>
            <p className="text-muted mb-3 fs-7">
              {t("pwa.install.description")}
            </p>
            <button
              onClick={handleInstallClick}
              className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2 h-min-auto"
            >
              <i className="bi bi-download"></i>
              <span>{t("pwa.install.button")}</span>
            </button>
          </div>
        );

      default:
        return (
          <div>
            <p className="text-muted mb-3 fs-7">
              {t("pwa.install.instructions")}
            </p>
            {deferredPrompt ? (
              <button
                onClick={handleInstallClick}
                className="btn btn-primary w-100 d-flex align-items-center justify-content-center gap-2 h-min-auto"
              >
                <i className="bi bi-download"></i>
                <span>{t("pwa.install.button")}</span>
              </button>
            ) : (
              <div className="text-muted fs-7">
                <p className="mb-0">{t("pwa.install.fallback")}</p>
              </div>
            )}
          </div>
        );
    }
  };

  return (
    <div
      className="position-fixed bottom-0 start-0 end-0 m-3 pwa-install-prompt"
      style={{ zIndex: 1055 }}
    >
      <div
        className="card shadow border-0 mx-auto"
        style={{ maxWidth: "24rem" }}
      >
        <div className="card-body">
          <div className="d-flex align-items-start justify-content-between mb-3">
            <div className="d-flex align-items-center gap-3">
              <div className="icon bg-info bg-gradient p-2">
                <i className="bi bi-phone text-white fs-5"></i>
              </div>
              <div>
                <h6 className="mb-1 fw-semibold">{t("pwa.title")}</h6>
                <p className="text-muted mb-0 fs-8">{t("pwa.subtitle")}</p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="btn-close"
              aria-label={t("common.close")}
            ></button>
          </div>

          {getInstallInstructions()}

          <div className="mt-4 pt-3 border-top">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-4">
                <span className="d-flex align-items-center fs-8 text-muted">
                  <span
                    className="bg-success rounded-circle me-2"
                    style={{ width: "0.5rem", height: "0.5rem" }}
                  ></span>
                  {t("pwa.features.offline")}
                </span>
                <span className="d-flex align-items-center fs-8 text-muted">
                  <span
                    className="bg-primary rounded-circle me-2"
                    style={{ width: "0.5rem", height: "0.5rem" }}
                  ></span>
                  {t("pwa.features.native")}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
